import { forwardRef } from 'react';
import refuseHeldEnter from '../../utils/refuseHeldEnter';
import './Button.css';

/**
 * Button — variants: primary, secondary, outline, encourage,
 * encourage-outline, ghost, danger, link. Sizes: sm, md, lg.
 *
 * Usage:
 *   <Button variant="primary" onClick={handleClick}>Save</Button>
 *   <Button variant="encourage" size="sm">Try Again</Button>
 *
 * type defaults to "button" (not the HTML native "submit") so a <Button>
 * dropped inside a <form> never triggers an implicit submit.
 *
 * `disabled` drops focus to <body> if the button has it when it turns on. For
 * a button that must keep focus while it can't act (a chat composer's Send,
 * see hooks/useChatComposer), pass `aria-disabled` instead: it gets the same
 * dimmed look, is announced as unavailable and stays focusable, and it
 * ignores activation the way `disabled` does. A click, Enter or Space runs
 * neither its own onClick nor an ancestor's, and a submit button doesn't
 * submit.
 *
 * `loading` is that case almost every time: a button shows its spinner
 * because it was just pressed, so it has focus when loading turns on. So
 * `loading` on its own never sets the native attribute: the button is
 * aria-disabled, as above, with the spinner. Do NOT also pass `disabled` for
 * the same state (`disabled={saving} loading={saving}`): an explicit
 * `disabled` is still honoured and brings the focus drop back. Pass both only
 * for a button that cannot have focus when its work starts, such as one that
 * opens a confirm dialog (the press that starts the work is in the dialog,
 * and the dialog's `returnFocusRef` relies on finding its opener disabled).
 * Neither state is reliably spoken on a button, so say "Saving…" where it
 * will be read: a <PendingStatus> beside the button, or a label that changes
 * inside a live region. `aria-busy` is not set for you: its job is to make
 * assistive tech hold announcements for a region until it settles, and on a
 * button whose own label is the cue that may hold back the cue (not measured
 * with a screen reader; a caller can still pass it).
 *
 * A held Enter presses a Button once. A natively disabled button lost focus
 * on the first press, which absorbed the key's repeats; one that keeps focus
 * would be pressed again as soon as it is available (utils/refuseHeldEnter).
 *
 * forwardRef (button-consolidation P0-2): several migrated call sites drive
 * imperative focus management (e.g. a confirm-dialog's default button, an
 * abandon-dialog's safe "Keep working" default) via a ref on the native
 * <button>. A plain function component would silently drop that ref.
 */
const Button = forwardRef(function Button({
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled = false,
  loading = false,
  children,
  className = '',
  onClick,
  onKeyDown,
  ...props
}, ref) {
  // The browser only refuses activation for `disabled`, which dispatches no
  // click at all. So an aria-disabled button refuses it here: Enter and Space
  // arrive as clicks, preventDefault cancels a submit button's form
  // submission, and stopPropagation keeps a clickable ancestor from acting.
  const ariaDisabled =
    loading || props['aria-disabled'] === true || props['aria-disabled'] === 'true';
  const refuse = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleKeyDown = (e) => {
    refuseHeldEnter(e);
    onKeyDown?.(e);
  };
  return (
    <button
      ref={ref}
      type={type}
      className={`btn btn--${variant} btn--${size} ${loading ? 'btn--loading' : ''} ${className}`}
      disabled={disabled}
      onClick={ariaDisabled ? refuse : onClick}
      onKeyDown={handleKeyDown}
      {...props}
      aria-disabled={loading && !disabled ? true : props['aria-disabled']}
    >
      {loading && <span className="btn__spinner" aria-hidden="true" />}
      {children}
    </button>
  );
});

export default Button;
